import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import VerifyPostModal from './VerifyPostModal';
import Page from '../app/layout/Page';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import QuillContext from '../editor/QuillContext';
import {
  BaseNewPostHistoryView,
  HistoryEmptyState,
  PostDoneButton,
  useCalculatedVersionChanges,
} from './history/NewPostHistoryView';
import ResponsiveDate from './history/ResponsiveDate';
import { gql } from '../graphql/generated/gql';
import InternalTopbar from '../topbar/InternalTopbar';
import { UIActions } from '../ui/uiSlice';
import VersionChanges from './history/VersionChanges';
import { isCommentOrPublicCommentThread } from '../utils/post/threadUtils';
import useNavigate from '../utils/router/useNavigate';
import useSlug from '../utils/router/useSlug';
import postPath from '../utils/url/postPath';

const POST_VERIFY_HISTORY_RANGE_QUERY = gql(/** @complexity 27 */ `
  query NewPostVerifyHistoryRange($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      updatedAt
      verificationEntries {
        id
        verifiedAt
        version
      }
      content(isLatest: true){
        id
        version
      }
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        insertedAt
        annotations {
          id
          ...BaseNewPostHistoryView_annotation
        }
        threads {
          id
          ...BaseNewPostHistoryView_thread
        }
        ...useCalculatedVersionChanges_history
      }
      ...VerifyPostModal_post
      ...BaseNewPostHistoryView_post
    }
    entityAssetToken(entityId: $postId, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface NewPostVerifyViewInnerProps {
  versionFrom: number;
  versionTo: number;
  postId: string;
}

const NewPostVerifyViewInner = ({
  versionFrom,
  versionTo,
  postId,
}: NewPostVerifyViewInnerProps) => {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data, error } = useQuery(POST_VERIFY_HISTORY_RANGE_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      postId,
      versionFrom,
      versionTo,
    },
  });

  const post = data?.post;

  const threads = useMemo(
    () => post?.history.threads.filter(isCommentOrPublicCommentThread) ?? [],
    [post?.history.threads],
  );

  const { content, allVersionChanges } = useCalculatedVersionChanges(
    post?.history ?? null,
  );

  const verificationEntry = post?.verificationEntries.at(0);
  const verificationEntryVersion = verificationEntry?.version;
  const currentPostVersion = post?.content.version;

  useEffect(() => {
    if (
      verificationEntryVersion != null &&
      currentPostVersion != null &&
      (versionFrom !== verificationEntryVersion ||
        versionTo !== currentPostVersion)
    ) {
      setSearchParams({
        histories: `${verificationEntryVersion}-${currentPostVersion}`,
      });
    }
  }, [
    versionFrom,
    versionTo,
    verificationEntryVersion,
    currentPostVersion,
    setSearchParams,
  ]);

  if (error) {
    return (
      <Page
        topbar={
          <InternalTopbar postActions={<PostDoneButton postId={postId} />} />
        }
      >
        <HistoryEmptyState />
      </Page>
    );
  }

  if (post == null) return null;

  return (
    <AssetTokenProvider entityAssetToken={data?.entityAssetToken}>
      <Page
        topbar={
          <InternalTopbar
            postActions={
              <>
                <Button
                  onClick={() =>
                    dispatch(
                      UIActions.openModal(VerifyPostModal, {
                        post,
                        onVerify: () => {
                          navigate(postPath(post));
                        },
                        close() {
                          dispatch(UIActions.closeModal());
                        },
                      }),
                    )
                  }
                  variant="primary"
                >
                  Reverify
                </Button>
                <PostDoneButton postId={postId} />
              </>
            }
          />
        }
      >
        <BaseNewPostHistoryView
          annotations={post.history.annotations}
          threads={threads}
          post={post}
          insertedAt={verificationEntry?.verifiedAt ?? post.history.insertedAt}
          editedAt={post.updatedAt}
          allVersionChanges={allVersionChanges}
          content={content}
          showAnnotationNewBadge
          topbar={
            <VersionChanges.PostLayoutTopbarContainer>
              <VersionChanges.Topbar>
                <VersionChanges.Meta>
                  {verificationEntry && (
                    <ResponsiveDate
                      date={verificationEntry.verifiedAt}
                      render={({ condensed, dateWithPreposition }) => {
                        return condensed
                          ? `Verified ${dateWithPreposition}`
                          : `Post last verified ${dateWithPreposition}`;
                      }}
                    />
                  )}
                </VersionChanges.Meta>
              </VersionChanges.Topbar>
            </VersionChanges.PostLayoutTopbarContainer>
          }
        />
      </Page>
    </AssetTokenProvider>
  );
};

const NewPostVerifyView = () => {
  const [postId] = useSlug('postSlug');

  const [searchParams] = useSearchParams();

  const historiesParam = searchParams.get('histories');
  const [, from, to] = historiesParam?.match(/(\d+)-(\d+)/) ?? [];

  if (from == null || to == null || from === to) {
    return (
      <Navigate
        to={postPath({ id: postId, title: '' })}
        replace
        state={{ verification: true }}
      />
    );
  }

  return (
    <QuillContext>
      <NewPostVerifyViewInner
        versionFrom={parseInt(from, 10)}
        versionTo={parseInt(to, 10)}
        postId={postId}
      />
    </QuillContext>
  );
};

export default NewPostVerifyView;
